"use client";

import type { FC, Ref } from "react";
import { mergeClasses, getDomain } from "@huub-web/utils";

import type { ClassNameProp } from "../../props";
import { Card, type CardProps } from "../Card";
import { Icon } from "../Icon";
import { Edges } from "../Edges";
import { Text } from "../Text";
import { useHover } from "../../hooks/useHover";

import {
  cardStyle,
  contentStyle,
  iconEdgeStyle,
  imageStyle,
  innerCardStyle,
  innerContentStyle,
  titleStyles,
} from "./LinkCard.css";

export type LinkCardProps = CardProps &
  ClassNameProp & {
    src?: string;
    url: string;
    title: string;
    description: string;
    subtitle?: string;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLAnchorElement>;
  };

/**
 * A card that displays a link with a title, description, and image.
 */
export const LinkCard: FC<LinkCardProps> = ({
  src,
  description,
  disabled,
  title,
  url,
  subtitle,
  children,
  className,
  ref,
  ...props
}) => {
  const { handleEnter, handleLeave, hasHover } = useHover({ disabled });

  return (
    <Card
      ref={ref}
      as="a"
      href={url}
      target="_blank"
      disabled={disabled}
      className={mergeClasses(cardStyle, className)}
      innerClassName={innerCardStyle}
      contentClassName={contentStyle}
      border="default"
      edgeDirection="horizontal"
      leading={
        src && (
          <div
            className={imageStyle}
            style={{ backgroundImage: `url(${src})` }}
          />
        )
      }
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      overflowHidden
      {...props}
    >
      {children}
      <Edges
        trailingClassName={iconEdgeStyle}
        trailing={<Icon icon="link" />}
        className={mergeClasses(innerContentStyle)}
        hideOverflow
      >
        <Text variant="surfaceSubtitle" ellipsis color="currentColor50">
          {subtitle || getDomain(url)}
        </Text>
        {title && (
          <Text
            as="h3"
            variant="bodyL"
            className={titleStyles({ hasHover })}
            clamp={2}
          >
            {title}
          </Text>
        )}
        <Text as="p" clamp={3} color="currentColor50">
          {description}
        </Text>
      </Edges>
    </Card>
  );
};
